<template>
  <form
      @submit.prevent="validate"
      v-if="booking.period"
  >
    <booking-summary
        :event="event"
        :booking="booking"
        class="mb-3"
    ></booking-summary>

    <HtmlText tag="h2" :text="$t('details')" class="h3 text-start py-2"></HtmlText>

    <div class="form-group mb-2">
      <input
          v-model="booking.email"
          required
          type="email"
          autocomplete="email"
          class="form-control"
          id="email"
          :placeholder="$t('enterEmailAddress')"
      >
    </div>

    <div class="form-group">
      <input
          v-model="booking.email_confirmation"
          required
          type="email"
          autocomplete="email"
          class="form-control"
          id="emailConfirmation"
          :placeholder="$t('confirmEmailAddress')"
      >
    </div>

    <template v-if="!booking.isFree() && event.sms_service">
      <hr>

      <div class="form-check mx-1 text-start small">
        <input
            v-model="booking.send_sms"
            type="checkbox"
            class="form-check-input"
            id="sendSms"
        >

        <label
            for="sendSms"
            class="form-check-label"
        >
          {{ $t('sendSms', [$n(event.sms_price, 'currency')]) }}

          <span v-if="event.sms_price">
            (+ {{ $n(event.sms_price, 'currency') }})
          </span>
        </label>
      </div>
    </template>

    <div
        v-if="booking.send_sms"
        class="form-group"
        :style="phoneInputStyle"
        ref="phoneInput"
    >
      <label
          for="telephone"
          class="visually-hidden"
      >
        {{ $t('telephone') }}
      </label>

      <vue-tel-input
          v-model="booking.telephone"
          inputId="telephone"
          mode="international"
          :preferred-countries="['NL', 'BE', 'DE', 'FR', 'ES']"
          input-classes="form-control"
          dynamicPlaceholder
          required
          validCharactersOnly
          @validate="telephoneValidate"
      ></vue-tel-input>
    </div>

    <hr>

    <i18n
        :path="agreeTermsKey"
        tag="small"
        class="d-inline-block text-start terms-and-conditions"
        for="termsAndConditions"
    >
      <a
          :href="termsAndConditions"
          slot="termsAndConditions"
          target="_blank"
          class="text-black"
      >{{ $t('termsAndConditions') }}</a>
    </i18n>

    <hr>

    <div class="d-flex justify-content-between align-items-center gap-4">
      <button
          type="button"
          class="btn btn-link btn-sm ms-n2"
          :class="{ 'disabled': saving }"
          :disabled="saving"
          @click="$router.go(-1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
             class="bi bi-arrow-left-short" viewBox="0 0 16 16">
          <path fill-rule="evenodd"
                d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5"/>
        </svg>
        <span class="ms-1">{{ $t('back') }}</span>
      </button>

      <button
          type="submit"
          class="btn btn-success flex-fill d-flex align-items-center justify-content-center"
          :class="{ 'disabled': saving }"
          :disabled="saving"
      >
        <template v-if="!saving">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-check"
                     viewBox="0 0 16 16" v-if="submitKey !== 'continue'"><path fill="white"
                                               d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/></svg>

          <span class="ms-1">{{ $t(submitKey) }}</span>
        </template>

        <template v-else>
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span class="ms-2">{{ $t('loading') }}</span>
        </template>
      </button>
    </div>
  </form>
</template>

<script>
import BookingSummary from '../components/BookingSummary';
import HtmlText from '../components/HtmlText';
import eventBus from '../eventBus';

export default {
  components: {
    BookingSummary,
    HtmlText
  },

  props: [
    'event',
    'booking',
    'saving'
  ],

  data() {
    return {
      telephoneValid: false
    }
  },

  computed: {
    totalLockers() {
      return this.booking.totalLockers;
    },

    /**
     * Is payment required for this booking?
     *
     * @returns {Boolean}
     */
    paymentRequired() {
      return this.booking.calculateTotalAmount(this.event) > 0;
    },

    /**
     * Get path to PDF of terms and conditions.
     *
     * @return {String}
     */
    termsAndConditions() {
      let country = 'UK';

      if (this.$i18n.locale === 'nl') {
        country = 'NL';
      } else if (this.$i18n.locale === 'es') {
        country = 'ES';
      }

      return `${process.env.BASE_URL}downloads/${country} AGV Locker Company 2024.pdf`;
    },

    /**
     * Get key for text to show as terms.
     *
     * @returns {String}
     */
    agreeTermsKey() {
      if (!this.paymentRequired) {
        return 'agreeTermsFinish';
      }

      return 'agreeTerms';
    },

    /**
     * Get key for text to show on submit button.
     *
     * @returns {String}
     */
    submitKey() {
      if (!this.paymentRequired) {
        return 'finish';
      }

      if (this.event.payment.select_method) {
        // Continue to payment method
        return 'continue';
      }

      return 'pay';
    },
    /**
     * Set the width of the phone input dropdown
     *
     * @returns {Object}
     */
    phoneInputStyle() {
      if(this.$refs.phoneInput) {
        return {
          '--vue-tel-input-width': `${this.$refs.phoneInput.offsetWidth}px`,
          '--vue-tel-input-height': `${this.$refs.phoneInput.offsetHeight}px`
        }
      }

      return {
        '--vue-tel-input-width': '326px',
        '--vue-tel-input-height': '47px'
      }
    }
  },

  watch: {
    '$route': 'init',

    'booking.send_sms': function (value) {
      if (!value) {
        this.booking.telephone = null;
      }
    }
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      if (!this.booking.period || this.totalLockers === 0) {
        this.$router.push('/event/' + this.event.code);
      }
    },

    /**
     * Update valid state of telephone.
     *
     * @param {Object} e
     */
    telephoneValidate(e) {
      this.telephoneValid = e.valid;
    },

    /**
     * Validate this form, if valid emit event 'ready' and go to next page.
     */
    validate() {
      eventBus.$emit('clearErrors');

      if (!this.booking.email || !this.booking.email_confirmation) {
        eventBus.$emit('error', this.$t('errors.noEmail'));
        return;
      }

      if (this.booking.email !== this.booking.email_confirmation) {
        eventBus.$emit('error', this.$t('errors.emailNoMatch'));
        return;
      }

      if (this.booking.send_sms && (!this.booking.telephone || !this.telephoneValid)) {
        eventBus.$emit('error', this.$t('errors.noTelephone'));
        return;
      }

      if (this.event.payment.select_method) {
        // Continue to payment method
        this.$emit('ready', this.booking);
        this.$router.push('/event/' + this.event.code + '/payment');
        return;
      }

      // Save booking
      this.$emit('ready', this.booking, true);
    }
  }
}
</script>
